import { createContext, useContext, useState, useCallback } from 'react';
import en from './en.json';
import fr from './fr.json';

export type Language = 'en' | 'fr';

const translations: Record<Language, typeof en> = { en, fr };

interface I18nContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    const saved = localStorage.getItem('draft-lang') as Language;
    return saved || 'en';
  });

  const setLang = useCallback((l: Language) => {
    setLangState(l);
    localStorage.setItem('draft-lang', l);
    document.documentElement.lang = l;
  }, []);

  const t = useCallback(
    (key: string): string => {
      const keys = key.split('.');
      let current: unknown = translations[lang];
      for (const k of keys) {
        if (current === null || current === undefined || typeof current !== 'object') {
          return key;
        }
        current = (current as Record<string, unknown>)[k];
      }
      return typeof current === 'string' ? current : key;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be inside I18nProvider');
  return ctx;
}
